import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { ShieldCheck, AlertTriangle } from "lucide-react";
import SovereignAnchor from "../components/SovereignAnchor";

const roleDestination = (role) => {
    if (role === "director") return "/app/director";
    if (role === "sports_admiral") return "/app/sports-cmdr";
    if (role === "td_commander") return "/app/td-cmdr";
    if (role === "founding_privateer") return "/app/privateer";
    return "/app/dashboard";
};

export default function CommissionExchange() {
    const { token } = useParams();
    const navigate = useNavigate();
    const { refresh } = useAuth();
    const [status, setStatus] = useState("verifying"); // verifying | ok | error
    const [message, setMessage] = useState("Verifying your Sovereign Commission...");
    const [commissioned, setCommissioned] = useState(null);
    const ran = useRef(false);

    useEffect(() => {
        if (ran.current) return; // guard against React Strict Mode double-invoke (would burn the token)
        ran.current = true;

        (async () => {
            try {
                const { data } = await api.post("/auth/commission-exchange", { token });
                // Re-fetch /me so AuthContext wins the race against its own initial fetch
                await refresh();
                setCommissioned(data);
                setStatus("ok");
                setMessage(`Welcome aboard, ${data.name || data.email}. Routing to your bridge...`);
                setTimeout(() => navigate(roleDestination(data.role), { replace: true }), 1400);
            } catch (e) {
                const msg = formatApiError(e.response?.data?.detail) || e.message || "Commission link could not be verified.";
                setStatus("error");
                setMessage(msg);
            }
        })();
    }, [token, navigate, refresh]);

    return (
        <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-6 grain-overlay relative">
            <div className="absolute inset-0 opacity-40 pointer-events-none">
                <div className="absolute top-[-30%] left-[-10%] w-[60%] h-[60%] rounded-full bg-[hsl(40_60%_45%/0.14)] blur-[120px]" />
                <div className="absolute bottom-[-30%] right-[-10%] w-[60%] h-[60%] rounded-full bg-[hsl(153_50%_25%/0.28)] blur-[120px]" />
            </div>

            <div className="relative w-full max-w-lg glass rounded-2xl p-8 md:p-12 text-center" data-testid="commission-exchange-card">
                <div className="flex justify-center mb-6">
                    <div className="w-14 h-14 rounded-full border border-[hsl(40_40%_35%/0.5)] bg-[hsl(151_55%_9%)] flex items-center justify-center">
                        {status === "verifying" && <SovereignAnchor size={28} strokeWidth={6} className="text-[hsl(45_85%_68%)] animate-pulse" />}
                        {status === "ok" && <ShieldCheck className="w-6 h-6 text-[hsl(45_85%_68%)]" />}
                        {status === "error" && <AlertTriangle className="w-6 h-6 text-[hsl(350_80%_70%)]" />}
                    </div>
                </div>

                <div className="text-[10px] uppercase tracking-[0.4em] text-muted-foreground">Sovereign Commission</div>
                <h1 className="font-heading text-4xl mt-2 gold-gradient-text tracking-tighter">
                    {status === "error" ? "Commission Blocked" : "Boarding the Site"}
                </h1>

                <p className="mt-4 text-muted-foreground leading-relaxed" data-testid="commission-message">
                    {message}
                </p>

                {status === "ok" && commissioned && (
                    <div className="mt-8 grid grid-cols-2 gap-3 text-left text-sm">
                        <div className="rounded-md border border-[hsl(40_40%_25%/0.25)] p-3">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Admiral</div>
                            <div className="font-medium mt-1">{commissioned.name}</div>
                        </div>
                        <div className="rounded-md border border-[hsl(40_40%_25%/0.25)] p-3">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Role</div>
                            <div className="font-medium mt-1 capitalize">{(commissioned.role || "").replace("_", " ")}</div>
                        </div>
                    </div>
                )}

                {status === "error" && (
                    <button
                        onClick={() => navigate("/login", { replace: true })}
                        data-testid="commission-fallback-login"
                        className="mt-8 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors"
                    >
                        Request a new link
                    </button>
                )}
            </div>
        </div>
    );
}
